import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Lock, ChevronRight, CheckCircle2, AlertTriangle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { useEncounter } from '@/contexts/EncounterContext';
import { emrService } from '@/services/emrService';
import { problemsService } from '@/services/problemsService';
import { avsService } from '@/services/avsService';
import { parseApiError } from '@/lib/apiErrors';
import AvsPreviewPanel from './AvsPreviewPanel';
import { cacheKeys } from '@/lib/cacheKeys';

type Step = 'review-diagnoses' | 'avs' | 'confirm';

interface SignEncounterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSigned?: () => void;
}

const SignEncounterDialog: React.FC<SignEncounterDialogProps> = ({
  open, onOpenChange, onSigned,
}) => {
  const { clinicId, encounterId, appointmentId, patientId } = useEncounter();
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('review-diagnoses');
  const [signing, setSigning] = useState(false);

  const { data: conditions = [] } = useQuery({
    queryKey: cacheKeys.patientConditions(patientId),
    queryFn: () => problemsService.getPatientConditions(clinicId, patientId),
  });
  const encounterDx = conditions.filter(c => c.category === 'encounter-diagnosis');

  const { data: avs } = useQuery({
    queryKey: cacheKeys.encounterAvs(encounterId),
    queryFn: () => avsService.getAvs(clinicId, encounterId),
    enabled: open,
  });

  const handleSign = async () => {
    setSigning(true);
    try {
      // 1. Generate AVS if it doesn't exist yet — patient deserves a summary.
      if (!avs) {
        try {
          await avsService.generateAvs(clinicId, encounterId);
        } catch (err) {
          // Non-fatal: signing should not be blocked by AVS generation issues
          // (e.g. backend route still in progress). Surface a warning.
          console.warn('AVS generation failed during sign-encounter:', err);
        }
      }

      // 2. Sign the encounter.
      await emrService.signEncounter(clinicId, encounterId);

      // 3. Move the appointment to completed (if linked).
      if (appointmentId) {
        try {
          await emrService.updateAppointmentStatus(clinicId, appointmentId, 'completed');
        } catch (err) {
          console.warn('Failed to mark appointment completed after sign:', err);
        }
      }

      await qc.invalidateQueries({ queryKey: cacheKeys.encounter(encounterId) });
      toast({ title: 'Visit signed and closed', variant: 'success' });
      onSigned?.();
      onOpenChange(false);
    } catch (err) {
      const e = parseApiError(err);
      toast({
        title: 'Failed to sign visit',
        description: e.message,
        variant: 'destructive',
      });
    } finally {
      setSigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-[720px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Lock className='h-4 w-4' /> Sign &amp; close visit
          </DialogTitle>
        </DialogHeader>

        <Stepper step={step} />

        <div className='py-4'>
          {step === 'review-diagnoses' && (
            <div className='space-y-3'>
              <h3 className='text-sm font-semibold text-slate-900'>Review encounter diagnoses</h3>
              {encounterDx.length === 0 ? (
                <div className='flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800'>
                  <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
                  <div>
                    No encounter diagnoses recorded. You can still sign without one,
                    but adding at least one diagnosis is best practice for billing
                    and care continuity.
                  </div>
                </div>
              ) : (
                <ul className='space-y-2'>
                  {encounterDx.map(c => (
                    <li key={c.id} className='flex items-center justify-between rounded-md border border-slate-200 p-2 text-sm'>
                      <div>
                        <span className='font-medium text-slate-900'>{c.display}</span>
                        <span className='ml-2 text-xs text-slate-500'>{c.code}</span>
                      </div>
                      <Badge variant='outline'>{c.status}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {step === 'avs' && (
            <div className='space-y-3'>
              <h3 className='text-sm font-semibold text-slate-900'>After-Visit Summary</h3>
              <p className='text-xs text-slate-500'>
                Generate, edit, and (optionally) publish the AVS now. You can also publish later
                from the AVS tab in the cockpit.
              </p>
              <AvsPreviewPanel />
            </div>
          )}

          {step === 'confirm' && (
            <div className='space-y-3'>
              <h3 className='text-sm font-semibold text-slate-900'>Confirm</h3>
              <div className='rounded-md bg-slate-50 p-3 text-sm text-slate-700 space-y-1'>
                <div className='flex items-center gap-2'>
                  <CheckCircle2 className='h-4 w-4 text-emerald-600' />
                  Encounter will be signed and locked.
                </div>
                <div className='flex items-center gap-2'>
                  <CheckCircle2 className='h-4 w-4 text-emerald-600' />
                  All notes / Rx / orders for this visit become read-only (edits require an addendum).
                </div>
                {appointmentId && (
                  <div className='flex items-center gap-2'>
                    <CheckCircle2 className='h-4 w-4 text-emerald-600' />
                    Appointment will be marked as completed.
                  </div>
                )}
                {!avs ? (
                  <div className='flex items-center gap-2'>
                    <CheckCircle2 className='h-4 w-4 text-emerald-600' />
                    AVS draft will be generated automatically.
                  </div>
                ) : avs.status === 'draft' ? (
                  <div className='flex items-center gap-2 text-amber-700'>
                    <AlertTriangle className='h-4 w-4' />
                    AVS draft is still unpublished. Publish it from the AVS tab after signing.
                  </div>
                ) : (
                  <div className='flex items-center gap-2'>
                    <CheckCircle2 className='h-4 w-4 text-emerald-600' />
                    AVS is already published.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          {step !== 'review-diagnoses' && (
            <Button
              variant='outline'
              onClick={() => setStep(step === 'confirm' ? 'avs' : 'review-diagnoses')}
              disabled={signing}
            >
              Back
            </Button>
          )}
          {step !== 'confirm' ? (
            <Button onClick={() => setStep(step === 'review-diagnoses' ? 'avs' : 'confirm')}>
              Next <ChevronRight className='ml-1 h-4 w-4' />
            </Button>
          ) : (
            <Button
              onClick={handleSign}
              disabled={signing}
              className='bg-brand-teal hover:bg-brand-teal/90'
            >
              {signing
                ? <><Loader2 className='mr-2 h-4 w-4 animate-spin' /> Signing…</>
                : <><Lock className='mr-2 h-4 w-4' /> Sign &amp; close visit</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Stepper: React.FC<{ step: Step }> = ({ step }) => {
  const items: { id: Step; label: string }[] = [
    { id: 'review-diagnoses', label: 'Diagnoses' },
    { id: 'avs', label: 'AVS' },
    { id: 'confirm', label: 'Confirm' },
  ];
  const idx = items.findIndex(i => i.id === step);
  return (
    <ol className='flex items-center gap-2 border-b border-slate-200 pb-3'>
      {items.map((it, i) => (
        <li key={it.id} className='flex items-center gap-2 text-xs'>
          <span className={
            i === idx
              ? 'rounded-full bg-brand-teal px-2 py-0.5 text-white'
              : i < idx
                ? 'rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700'
                : 'rounded-full bg-slate-100 px-2 py-0.5 text-slate-500'
          }>
            {i + 1}. {it.label}
          </span>
          {i < items.length - 1 && <ChevronRight className='h-3 w-3 text-slate-300' />}
        </li>
      ))}
    </ol>
  );
};

export default SignEncounterDialog;
